/*
 * The site's one named author, as a single authoritative `Person` node.
 *
 * Every published article references this node by `@id` (see `authorReference()` and
 * `buildArticleSchema` in ./articleSchema.js) instead of repeating an author string.
 * One entity referenced 45 times resolves; 45 literals do not. The page that DEFINES
 * the node — the only place the full Person is emitted — is /phillip-joe.
 *
 * EVERYTHING HERE WAS SUPPLIED BY PHILLIP JOE. Nothing is inferred, rounded, or
 * rewritten. The bio below is verbatim; `authorProfile.test.js` pins it character for
 * character so an "improvement" cannot quietly reword what he said about himself.
 *
 * If a schema.org field wants data we do not have — employer history, education,
 * awards, certifications, years of experience, birth date — the field is OMITTED.
 * An empty field costs nothing. A fabricated credential is the exact failure mode
 * E-E-A-T work exists to avoid, and it is not recoverable once it is indexed.
 */

import { AUTHOR_PATH, AUTHOR_PERSON_ID, AUTHOR_URL, ORGANIZATION_ID, SITE_URL } from '../content/siteEntityIds.js';

export { AUTHOR_PATH, AUTHOR_PERSON_ID, AUTHOR_URL };

export const AUTHOR_NAME = 'Phillip Joe';

/* Supplied as "Founder, BlueBear.ai · AI engineer". Kept as two discrete titles so
 * neither half is lost to a separator a parser does not understand. */
export const AUTHOR_JOB_TITLES = ['Founder, BlueBear.ai', 'AI engineer'];

export const AUTHOR_LOCATION = 'Los Angeles';

/* A real, verified profile. Never a placeholder: a sameAs pointing at nothing is
 * worse than no sameAs, because it teaches an entity resolver a false edge. */
export const AUTHOR_LINKEDIN_URL = 'https://www.linkedin.com/in/phillip-joe-31966813/';

export const AUTHOR_IMAGE_BASE = '/media/bluebear-people/phillip-joe';
export const AUTHOR_IMAGE = `${AUTHOR_IMAGE_BASE}-400.jpg`;
export const AUTHOR_IMAGE_WIDTH = 400;
export const AUTHOR_IMAGE_HEIGHT = 400;
export const AUTHOR_IMAGE_ALT = 'Phillip Joe, founder of BlueBear.ai';

/* Verbatim. Do not rewrite, trim, or "polish". */
export const AUTHOR_BIO_PARAGRAPHS = [
  "I'm Phillip Joe, founder of BlueBear.ai and an AI engineer based in Los Angeles.",
  'I started BlueBear after spending years building software, automation, and AI systems for real businesses. What began as consulting turned into something much larger: I kept running into infrastructure that didn\'t exist yet, so I started building it.',
  'Today, I work on AI agents that can operate real business systems, model orchestration, enterprise integrations, and tools that make increasingly capable AI systems more useful in the real world.',
  "I'm especially interested in the point where AI stops being something you chat with and starts becoming something that can actually do work—safely, reliably, and with enough context to make good decisions.",
  "Most days, I'm building, testing, talking with customers, or running far too many AI coding agents at once.",
];

export const AUTHOR_BIO = AUTHOR_BIO_PARAGRAPHS.join('\n\n');

/* A one-line third-person summary for meta descriptions and card subtitles, built
 * only from the facts above. */
export const AUTHOR_SUMMARY = 'Phillip Joe is the founder of BlueBear.ai and an AI engineer based in Los Angeles, working on AI agents that operate real business systems, model orchestration, and enterprise integrations.';

/* What he actually works on. Every line below describes a boundary this codebase and
 * the BlueBear gateway already implement — the same language the product pages use.
 * No capability is claimed here that is not claimed on /features, /mcp-gateway or
 * /services. */
export const AUTHOR_WORK_AREAS = [
  {
    title: 'The BlueBear MCP Gateway',
    detail: 'Identity, workspace policy, credential resolution, approvals, tool execution, and audit evidence placed around Model Context Protocol connections, so tool authority is evaluated at an execution boundary instead of being carried in prompt text.',
    href: '/mcp-gateway',
  },
  {
    title: 'Model routing and orchestration',
    detail: 'Routing policy that decides which model runs a step from declared capability, latency, cost, compliance boundary, and fallback rules — and keeps the effective policy beside the request evidence so a routing decision can be reviewed after the fact.',
    href: '/services/optimization',
  },
  {
    title: 'Enterprise integrations',
    detail: 'Connecting agents to the systems a business already runs on through assigned connections, permitted actions, and retained execution receipts rather than broad standing access.',
    href: '/services/agents',
  },
  {
    title: 'Deployment and cost boundaries',
    detail: 'Managed infrastructure, customer-owned cloud accounts, private clusters, and on-premise environments, with Budget Manager scopes and cost-per-accepted-outcome measurement so operators can govern AI spend against business results.',
    href: '/services/deploy',
  },
];

/* Topics he genuinely works on, drawn from the work areas above. */
export const AUTHOR_KNOWS_ABOUT = [
  'AI agents',
  'Model routing',
  'Model Context Protocol',
  'Enterprise system integration',
  'AI agent governance',
  'AI infrastructure',
];

function absolute(assetPath) {
  return /^https?:\/\//.test(assetPath) ? assetPath : `${SITE_URL}${assetPath.startsWith('/') ? assetPath : `/${assetPath}`}`;
}

/*
 * The visible byline an article prints above its body.
 *
 * `authorReference()` below is what a crawler reads; this is what a READER reads, and
 * E-E-A-T is assessed on both. It is derived from AUTHOR_NAME and AUTHOR_JOB_TITLES so
 * the name and title a visitor sees can never drift from the ones in the Person node —
 * there is no second copy of either string to update.
 */
export function authorByline() {
  return `By ${AUTHOR_NAME} — ${AUTHOR_JOB_TITLES.join(' · ')}`;
}

/*
 * The reference an article carries. It is a reference, not a copy: `@id` does the
 * entity work, while `@type`, `name` and `url` are the minimum that keeps the node
 * self-describing for a consumer that only ever sees this one page. The biography,
 * image, sameAs and worksFor edges live on the profile page, once.
 */
export function authorReference() {
  return {
    '@type': 'Person',
    '@id': AUTHOR_PERSON_ID,
    name: AUTHOR_NAME,
    url: AUTHOR_URL,
  };
}

/*
 * The full node. Emitted only where it is defined: /phillip-joe, and /about, which
 * names him as founder.
 */
export function buildPersonSchema() {
  return {
    '@type': 'Person',
    '@id': AUTHOR_PERSON_ID,
    name: AUTHOR_NAME,
    url: AUTHOR_URL,
    /* A URL, deliberately, not an `@id` reference. This node is also emitted on
     * /about, where a `#webpage` node for the profile page does not exist — pointing
     * at it there would leave a dangling edge, which is the exact bug this site
     * already carries corpus-wide. A plain URL says the same thing and resolves
     * everywhere. */
    mainEntityOfPage: AUTHOR_URL,
    jobTitle: AUTHOR_JOB_TITLES,
    description: AUTHOR_BIO,
    image: {
      '@type': 'ImageObject',
      url: absolute(AUTHOR_IMAGE),
      width: AUTHOR_IMAGE_WIDTH,
      height: AUTHOR_IMAGE_HEIGHT,
      caption: AUTHOR_IMAGE_ALT,
    },
    homeLocation: { '@type': 'Place', name: AUTHOR_LOCATION },
    worksFor: { '@id': ORGANIZATION_ID },
    knowsAbout: AUTHOR_KNOWS_ABOUT,
    sameAs: [AUTHOR_LINKEDIN_URL],
  };
}
